import { useRef, useEffect, useState, useCallback } from 'react';
import { authedApiFetch } from '../../utils/Api';

/**
 * Custom hook for initiating Composio OAuth flow.
 *
 * @param {Object} options
 * @param {string} options.tenantId - Tenant ID for API calls
 * @returns {Object} { initiateComposioOAuth, oAuthResult }
 */
export function useComposioOAuth({
  tenantId
}) {
  const [oAuthResult, setOAuthResult] = useState(null);
  const popupClosedIntervalRef = useRef(null);

  const clearPopupInterval = () => {
    if (popupClosedIntervalRef.current) {
      clearInterval(popupClosedIntervalRef.current);
    }
  }

  // Cleanup popup closed polling interval on unmount
  useEffect(() => {
    return () => {
      clearPopupInterval();
    };
  }, []);

  const popupMessageListener = (event) => {
    // Check event source against expected popup window
    if (process.env.NODE_ENV !== 'development' && event.origin !== process.env.REACT_APP_API_URL) {
      console.error(
        'Received message from untrusted origin:',
        event.origin,
        'message:',
        event.data,
        'Ignoring message.'
      );
      return;
    }

    // Ignore events that don't match expected format
    if (!event.data || (event.data.success === undefined)) {
      console.error('Received invalid message from popup:', event.data);
      return;
    }

    const { success, data } = event.data;
    setOAuthResult({
      success,
      data
    });

    clearPopupInterval();
    removeWindowListener();
  };

  const addWindowListener = () => {
    window.addEventListener("message", popupMessageListener, false);
  }

  const removeWindowListener = () => {
    window.removeEventListener("message", popupMessageListener, false);
  }

  const initiateComposioOAuth = useCallback(
    async (serviceId, settings = {}) => {
      try {
        setOAuthResult(null);

        // Build params object with service_id, tenant_id, and any additional settings
        const params = {
          service_id: serviceId,
          tenant_id: tenantId,
          ...settings
        };

        // Call the backend to get the OAuth URL using authedApiFetch
        const data = await authedApiFetch({
          endpoint: `/oauth/authorize`,
          method: 'GET',
          params: params,
        });

        if (!data.auth_url) {
          throw new Error('No authorization URL received');
        }

        // Open OAuth in popup with complete URL
        const windowFeatures = 'width=600,height=700,scrollbars=yes';
        const popup = window.open(
          data.auth_url,
          'Connect Service',
          windowFeatures
        );

        // What to do if popup is blocked. See:
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/open#return_value
        if (!popup) {
          setOAuthResult({
            success: false,
            error: 'Popup was blocked'
          });
          return;
        }

        // Start polling for popup closure
        popupClosedIntervalRef.current = setInterval(() => {
          if (popup.closed) {
            clearInterval(popupClosedIntervalRef.current);
            // If no result has been set (no message received), treat as error
            if (!oAuthResult) {
              setOAuthResult({
                success: false,
                error: 'Popup was closed before a response was received'
              });
            }
            // If result exists, do nothing (already handled by message)
          }
        }, 500);

        // Add window event listener waiting for message from popup
        addWindowListener();
      } catch (error) {
        removeWindowListener();
        setOAuthResult({
          success: false,
          error: error.message
        });
      }
    },
    [tenantId]
  );

  return {
    initiateComposioOAuth,
    oAuthResult
  };
}
